import React, { useEffect, useState } from 'react';
import { FormField } from './FormField';
import { CalendarIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon, InfoIcon, CreditCardIcon, WalletIcon, CreditCardIcon as CardIcon } from 'lucide-react';
import { MetadataEditor, MetadataItem } from './MetadataEditor';
import { RedirectLinkField } from './RedirectLinkField';
export type PaymentProvider = {
  id: string;
  name: string;
  icon: React.ReactNode;
};
interface CommonPaymentOptionsProps {
  metadata: MetadataItem[];
  redirectUrl: string;
  expiryDate: string;
  onMetadataChange: (metadata: MetadataItem[]) => void;
  onRedirectUrlChange: (url: string) => void;
  onExpiryDateChange: (date: string) => void;
  hideExpiry?: boolean;
  hideRedirect?: boolean;
  selectedPaymentProviders?: string[];
  onSelectedPaymentProvidersChange?: (providers: string[]) => void;
}
export function CommonPaymentOptions({
  metadata,
  redirectUrl,
  expiryDate,
  onMetadataChange,
  onRedirectUrlChange,
  onExpiryDateChange,
  hideExpiry = false,
  hideRedirect = false,
  selectedPaymentProviders = [],
  onSelectedPaymentProvidersChange
}: CommonPaymentOptionsProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [optionsSectionExpanded, setOptionsSectionExpanded] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [showPaymentProviders, setShowPaymentProviders] = useState(false);
  // Available payment providers
  const paymentProviders: PaymentProvider[] = [{
    id: 'card-payments',
    name: 'Card Payments',
    icon: <CreditCardIcon size={16} className="text-blue-600" data-id="element-205" />
  }, {
    id: 'alipay',
    name: 'Alipay',
    icon: <WalletIcon size={16} className="text-blue-500" data-id="element-206" />
  }, {
    id: 'wechatpay',
    name: 'WechatPay',
    icon: <WalletIcon size={16} className="text-green-500" data-id="element-207" />
  }, {
    id: 'rabbit-card',
    name: 'Rabbit Card',
    icon: <CardIcon size={16} className="text-purple-500" data-id="element-208" />
  }, {
    id: 'linepay',
    name: 'LinePay',
    icon: <WalletIcon size={16} className="text-green-600" data-id="element-209" />
  }];
  // In a real app, this would come from settings/localStorage/API
  const defaultExpiryDays = 30;
  // Calculate days between now and expiry date
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => parseInt(part, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part to get accurate day difference
      const timeDiff = expiryDateObj.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setExpiryDays(daysDiff);
    }
  }, [expiryDate]);
  // Set default expiry date to defaultExpiryDays from now when component mounts
  useEffect(() => {
    if (!expiryDate) {
      const date = new Date();
      date.setDate(date.getDate() + defaultExpiryDays);
      const formattedDate = formatDate(date);
      onExpiryDateChange(formattedDate);
    }
  }, [expiryDate, onExpiryDateChange, defaultExpiryDays]);
  // Format date as MM/DD/YYYY
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  // Parse date string to Date object
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  // Generate calendar for date picker
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = showDatePicker ? expiryDate ? parseDate(expiryDate) : today : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() - 1);
    // Force re-render
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() + 1);
    // Force re-render
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    if (date) {
      onExpiryDateChange(formatDate(date));
      setShowDatePicker(false);
    }
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const togglePaymentProvider = (providerId: string) => {
    if (!onSelectedPaymentProvidersChange) return;
    let updatedProviders: string[];
    if (selectedPaymentProviders.includes(providerId)) {
      updatedProviders = selectedPaymentProviders.filter(id => id !== providerId);
    } else {
      updatedProviders = [...selectedPaymentProviders, providerId];
    }
    onSelectedPaymentProvidersChange(updatedProviders);
  };
  const hasMetadata = metadata && metadata.length > 0;
  const hasRedirectUrl = redirectUrl && redirectUrl.length > 0;
  const hasExpiryDate = expiryDate && expiryDate.length > 0;
  const hasSelectedProviders = selectedPaymentProviders && selectedPaymentProviders.length > 0;
  const hasAnyData = hasMetadata || hasRedirectUrl || hasExpiryDate || hasSelectedProviders;
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-210">
      <button className="w-full text-left flex items-center justify-between text-lg font-medium text-gray-800 mb-2" onClick={() => setOptionsSectionExpanded(!optionsSectionExpanded)} data-id="element-211">
        <div className="flex items-center" data-id="element-212">
          <SettingsIcon size={18} className="mr-2 text-blue-600" data-id="element-213" />
          <span data-id="element-214">Additional options</span>
        </div>
        {optionsSectionExpanded ? <ChevronDownIcon size={20} className="text-gray-500" data-id="element-215" /> : <ChevronRightIcon size={20} className="text-gray-500" data-id="element-216" />}
      </button>

      {/* Show summary when collapsed */}
      {!optionsSectionExpanded && hasAnyData && <div className="text-sm text-gray-600 ml-7 mt-1" data-id="element-217">
          {[hasExpiryDate && !hideExpiry ? `Expires: ${expiryDate}` : null, hasRedirectUrl && !hideRedirect ? 'Has redirect URL' : null, hasMetadata ? `${metadata.length} metadata ${metadata.length === 1 ? 'field' : 'fields'}` : null, hasSelectedProviders ? `${selectedPaymentProviders.length} payment ${selectedPaymentProviders.length === 1 ? 'method' : 'methods'} selected` : null].filter(Boolean).join(', ')}
        </div>}

      {optionsSectionExpanded && <>
          {!hideExpiry && <div className="relative mt-4" data-id="element-218">
              <FormField label="Payment link expiration" tooltip="When this payment link will no longer be valid" data-id="element-219">
                <div className="relative" data-id="element-220">
                  <input type="text" className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" value={expiryDate} onChange={e => onExpiryDateChange(e.target.value)} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-221" />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-222">
                    <CalendarIcon size={16} className="text-gray-400" data-id="element-223" />
                  </div>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-224">
                    {showDatePicker ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-225" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-226" />}
                  </div>
                </div>
                {/* Expiry information moved from bottom bar */}
                <div className="mt-2 text-sm" data-id="element-227">
                  {expiryDate && <div className="flex items-start text-gray-600" data-id="element-228">
                      <InfoIcon size={14} className="mr-1.5 mt-0.5 text-blue-600 flex-shrink-0" data-id="element-229" />
                      <div data-id="element-230">
                        <span data-id="element-231">
                          This payment link will expire in {expiryDays} days (
                          {expiryDate})
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5" data-id="element-232">
                          Your merchant account has a default expiry of{' '}
                          {defaultExpiryDays} days configured, you can update
                          this under your payment link settings.
                        </span>
                      </div>
                    </div>}
                </div>
              </FormField>
              {/* Date Picker Dropdown */}
              {showDatePicker && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-full" data-id="element-233">
                  <div className="flex justify-between items-center mb-2" data-id="element-234">
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-235">
                      <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-236" />
                    </button>
                    <div className="font-medium" data-id="element-237">
                      {calendar.month.toLocaleString('default', {
                month: 'long',
                year: 'numeric'
              })}
                    </div>
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-238">
                      <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-239" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-240">
                    <div className="text-gray-500" data-id="element-241">Su</div>
                    <div className="text-gray-500" data-id="element-242">Mo</div>
                    <div className="text-gray-500" data-id="element-243">Tu</div>
                    <div className="text-gray-500" data-id="element-244">We</div>
                    <div className="text-gray-500" data-id="element-245">Th</div>
                    <div className="text-gray-500" data-id="element-246">Fr</div>
                    <div className="text-gray-500" data-id="element-247">Sa</div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center" data-id="element-248">
                    {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                              w-8 h-8 rounded-full flex items-center justify-center text-sm
                              ${isSelected(date) ? 'bg-blue-600 text-white' : ''}
                              ${isToday(date) && !isSelected(date) ? 'border border-blue-600 text-blue-600' : ''}
                              ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                            `} onClick={() => handleDateSelect(date)} data-id="element-249">
                            {date.getDate()}
                          </button> : <div key={index} className="w-8 h-8" data-id="element-250"></div>))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-251">
                    <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
              const date = new Date();
              date.setDate(date.getDate() + 7);
              onExpiryDateChange(formatDate(date));
              setShowDatePicker(false);
            }} data-id="element-252">
                      7 days
                    </button>
                    <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
              const date = new Date();
              date.setDate(date.getDate() + 30);
              onExpiryDateChange(formatDate(date));
              setShowDatePicker(false);
            }} data-id="element-253">
                      30 days
                    </button>
                    <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
              const date = new Date();
              date.setDate(date.getDate() + 90);
              onExpiryDateChange(formatDate(date));
              setShowDatePicker(false);
            }} data-id="element-254">
                      90 days
                    </button>
                  </div>
                </div>}
            </div>}

          {/* Payment Providers Selection */}
          {onSelectedPaymentProvidersChange && <div className="mt-4" data-id="element-255">
              <div className="flex items-center justify-between mb-2" data-id="element-256">
                <div className="flex items-center" data-id="element-257">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-258">
                    Limit payment methods
                  </h3>
                  <div className="relative ml-1.5" data-id="element-259">
                    <div className="text-gray-400 hover:text-gray-500 cursor-help" onMouseEnter={() => setShowPaymentProviders(true)} onMouseLeave={() => setShowPaymentProviders(false)} data-id="element-260">
                      <InfoIcon size={14} data-id="element-261" />
                    </div>
                    {showPaymentProviders && <div className="absolute z-10 w-64 px-3 py-2 text-sm text-left font-normal text-gray-700 bg-white border border-gray-200 rounded-md shadow-lg -top-2 left-6" data-id="element-262">
                        Optionally select specific payment methods to limit what
                        your customer can use. If none are selected, all
                        available payment methods will be shown.
                      </div>}
                  </div>
                </div>
                {selectedPaymentProviders.length > 0 && <button type="button" className="text-xs text-blue-600 hover:text-blue-800" onClick={() => onSelectedPaymentProvidersChange([])} data-id="element-263">
                    Clear all
                  </button>}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-264">
                {paymentProviders.map(provider => <div key={provider.id} className={`
                      flex items-center p-3 border rounded-md cursor-pointer transition-colors
                      ${selectedPaymentProviders.includes(provider.id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
                    `} onClick={() => togglePaymentProvider(provider.id)} data-id="element-265">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={selectedPaymentProviders.includes(provider.id)} onChange={() => togglePaymentProvider(provider.id)} data-id="element-266" />
                    <div className="ml-3 flex items-center" data-id="element-267">
                      <span className="mr-2" data-id="element-268">{provider.icon}</span>
                      <span className="text-sm font-medium text-gray-700" data-id="element-269">
                        {provider.name}
                      </span>
                    </div>
                  </div>)}
              </div>
              {selectedPaymentProviders.length > 0 && <div className="mt-2 text-xs text-gray-500 flex items-center" data-id="element-270">
                  <InfoIcon size={12} className="mr-1.5 text-blue-500 flex-shrink-0" data-id="element-271" />
                  Only the selected payment methods will be available to your
                  customer
                </div>}
            </div>}

          {!hideRedirect && <div className="mt-4" data-id="element-272">
              <RedirectLinkField value={redirectUrl} onChange={onRedirectUrlChange} data-id="element-273" />
            </div>}

          <div className="mt-4" data-id="element-274">
            <MetadataEditor metadata={metadata} onChange={onMetadataChange} data-id="element-275" />
          </div>
        </>}
    </div>;
}